import { useEffect, useRef, useState } from "react";

import { authProvider } from "../context/authProvider";
import { useNavTabs } from "../hooks/inventario/useNavTabs";

import { LoadingSpinner } from "../components/LoadingSpinner";
import { AuthenticationState, OktaIDP } from "../components/OktaIDP";
import { InventarioSidebar } from "../components/ModuloInventario/InventarioSidebar";
import { InventarioContent } from "../components/ModuloInventario/InventarioContent";
import { InventarioHeaderActions } from "../components/ModuloInventario/InventarioHeaderActions";
import { InventarioContentStore } from "../components/ModuloInventario/InventarioContentStore";
import { InventarioSectionTitle } from "../components/ModuloInventario/InventarioSectionTitle";
import { ProductSearchBar } from "../components/ModuloInventario/ProductSearchBar";
import { BtnGoBack } from "../components/Buttons/BtnGoBack";
import { useStoreOperations } from "../hooks/inventario/useStoreOperations";
import { useProductOperations } from "../hooks/inventario/useProductOperations";
import { useStores } from "../context/StoresContext";
import { useInventarioTabManager } from "../hooks/inventario/useInventarioTabManager";
import { TAB_KEY, TAB_NAMES, VALID_TABS } from "../const/inventario";
import { UseAdmin } from "../hooks/UseAdmin";

/**
 * Página principal del módulo de Inventario correspondiente a Administracion.
 * Gestiona la navegación entre tabs (Items y Almacenes) y filtros por categorias de almacenes
 */

export const AdministracionInventario = () => {
  //* ESTADOS LOCALES
  const [activeTab, setActiveTab] = useState(() => {
    if (typeof window === "undefined") return "Items";

    const stored = window.sessionStorage.getItem(TAB_KEY);

    if (stored && VALID_TABS.some((tab) => tab.name === stored)) {
      return stored;
    }

    return "Items";
  });

  const hasInitializedRef = useRef(false);

  //* Persistir cambios de tab
  useEffect(() => {
    if (typeof window === "undefined") return;

    window.sessionStorage.setItem(TAB_KEY, activeTab);
  }, [activeTab]);

  //* HOOKS
  const tabs = useNavTabs();
  const { brandfcNombre: brand } = UseAdmin();
  const { isLoading } = useInventarioTabManager(activeTab);
  const { selectedStore } = useStores();
  const { operations: storeOperations, states: storeStates } = useStoreOperations();
  const { states: productStates } = useProductOperations();

  //* DERIVADOS
  const isStoreTab = activeTab === TAB_NAMES.stores;
  const isFetching = storeStates.fetchState.loading || productStates.fetchState.loading;
  const showLoading = !brand || isFetching || isLoading;

  //* fetch stores
  useEffect(() => {
    // No ejecutar si brand no está disponible
    if (!brand) {
      // eslint-disable-next-line no-console
      console.log("⏳ [AdministracionInventario] Esperando cargar marca...");
      return;
    }

    if (hasInitializedRef.current) return;

    const loadStores = async () => {
      try {
        hasInitializedRef.current = true;

        const result = await storeOperations.fetchStores();

        if (!result.success) {
          throw new Error(result.error);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error loading stores:", error);
        hasInitializedRef.current = false;
      }
    };

    loadStores();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brand]);

  /**
   * Al cambiar de tab, resetea la categoría seleccionada
   */
  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
  };

  //  TÍTULOS DINÁMICOS
  /**
   * Determina el título según el tab activo
   */
  const getSectionTitle = () => {
    switch (activeTab) {
      case TAB_NAMES.items:
        return TAB_NAMES.items;
      case TAB_NAMES.stores:
        return selectedStore;
      case TAB_NAMES.periodicity:
        return TAB_NAMES.periodicity;
      default:
        return TAB_NAMES.items;
    }
  };

  /**
   * Determina el subtítulo según el contexto
   */
  const getSectionSubtitle = () => {
    if (activeTab === TAB_NAMES.stores) {
      return selectedStore === "Todos"
        ? "Se muestran todos los ítems"
        : `Se muestran los ítems contenidos en ${selectedStore} y otros almacenes`;
    }
    return "";
  };

  return (
    <OktaIDP provider={authProvider}>
      {({ authenticationState }) => {
        const isAuthenticated = authenticationState === AuthenticationState.Authenticated;

        if (isAuthenticated) {
          return (
            <div className="body-color">
              {/*  NAVIGATION HEADER  */}
              <div className="title-name-hub border-none">
                <div className="icon-goback-title-hub">
                  <BtnGoBack caption="Volver a Administración" border={false} />
                </div>

                {/* Tabs de navegación */}
                <InventarioHeaderActions
                  tabs={tabs}
                  activeTab={activeTab}
                  onChangeTab={handleTabChange}
                />
              </div>

              {/*  MAIN CONTENT  */}
              <div className="wrapper-main-content">
                {showLoading ? (
                  <LoadingSpinner />
                ) : (
                  <div className="container-body-hub pt-none">
                    <div className={isStoreTab ? "wrapper-body-hub-store" : "wrapper-body-hub"}>
                      <div className="body-hub-sidebar">
                        <InventarioSidebar activeTab={activeTab} />
                      </div>

                      <div className="body-hub-main-content">
                        {/* Header del contenido */}
                        <div className="body-hub-main-content-header">
                          <InventarioSectionTitle
                            title={getSectionTitle()}
                            subtitle={getSectionSubtitle()}
                          />
                          <ProductSearchBar />
                        </div>

                        {/* Contenido según tab activo */}
                        <div className="body-hub-main-content-body">
                          {activeTab === TAB_NAMES.items && <InventarioContent />}
                          {activeTab === TAB_NAMES.stores && <InventarioContentStore />}
                          {activeTab === TAB_NAMES.periodicity && (
                            <div>Periodicidad de conteo (TODO)</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        }
      }}
    </OktaIDP>
  );
};
